import { useMemo, useState } from "react";
import { ChevronRight, ChevronDown, Circle } from "lucide-react";
import { day } from "./_shared";

// Flatten the tree iteratively (no recursive component — avoids the CRA/Babel
// dev-transform stack overflow that self-referencing components trigger).
function flatten(root, collapsed) {
  if (!root) return [];
  const out = [];
  const stack = [{ node: root, depth: 0 }];
  while (stack.length) {
    const { node, depth } = stack.pop();
    const kids = node.children || [];
    out.push({ node, depth, hasKids: kids.length > 0 });
    if (kids.length && !collapsed.has(node.user_id)) {
      // push in reverse so the first child is processed first
      for (let i = kids.length - 1; i >= 0; i--) {
        stack.push({ node: kids[i], depth: depth + 1 });
      }
    }
  }
  return out;
}

export default function ReferralTree({ tree, perLevel, totals, potential }) {
  const levels = perLevel || [];
  const [collapsed, setCollapsed] = useState(() => new Set());
  const rows = useMemo(() => flatten(tree, collapsed), [tree, collapsed]);

  const toggle = (uid) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    return next;
  });

  return (
    <div className="space-y-4" data-testid="admin-referral-tree">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {levels.map((l) => (
          <div key={l.level} className="rounded-xl border border-border bg-card p-3 text-center" data-testid={`admin-level-${l.level}`}>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Ebene {l.level}{l.compensated ? " · Provision" : ""}</p>
            <p className="mt-1 font-display text-xl font-semibold text-foreground">{l.user_count}</p>
            <p className="text-[11px] text-emerald-600">{l.active_count} aktiv</p>
          </div>
        ))}
      </div>

      {(totals || potential) && (
        <div className="flex flex-wrap gap-2 text-xs">
          {totals && (
            <>
              <span className="rounded-full bg-emerald-100 px-3 py-1 font-medium text-emerald-700">Ausgezahlt: {((totals.paid_cents || 0) / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })}</span>
              <span className="rounded-full bg-amber-100 px-3 py-1 font-medium text-amber-700">Ausstehend: {((totals.pending_cents || 0) / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })}</span>
            </>
          )}
          {potential && potential.projected_total_network > 0 && (
            <span className="rounded-full bg-sky-100 px-3 py-1 font-medium text-sky-700">Potenzial: {potential.projected_total_network} Netzwerk-User</span>
          )}
        </div>
      )}

      {rows.length ? (
        <div className="max-h-[480px] overflow-auto rounded-xl border border-border bg-background p-1">
          {rows.map(({ node, depth, hasKids }) => (
            <div
              key={node.user_id}
              className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-secondary/50"
              style={{ paddingLeft: `${depth * 16 + 8}px` }}
              data-testid={`admin-tree-node-${node.user_id}`}
            >
              {hasKids ? (
                <button onClick={() => toggle(node.user_id)} className="shrink-0 text-muted-foreground" data-testid={`admin-tree-toggle-${node.user_id}`}>
                  {collapsed.has(node.user_id) ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
              ) : (
                <Circle className={`h-2 w-2 shrink-0 ${node.is_active ? "fill-emerald-500 text-emerald-500" : "fill-muted-foreground/40 text-muted-foreground/40"}`} />
              )}
              <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                {node.name || "(unbekannt)"}
                <span className="ml-1.5 text-xs text-muted-foreground">{node.email}</span>
              </span>
              <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-muted-foreground">{node.role}</span>
              {node.own_direct_count > 0 && (
                <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-700" title="Eigene Direkt-Empfehlungen">+{node.own_direct_count}</span>
              )}
              <span className="hidden text-[11px] text-muted-foreground sm:inline">{node.joined_at ? day(node.joined_at) : ""}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Noch kein Netzwerk vorhanden.</p>
      )}
    </div>
  );
}
